import { breakpoint } from '@/helpers/BreakpointHelper';
import { isBrowser } from '@/helpers/BrowserHelper';
import React, { FC } from 'react';
import { createPortal } from 'react-dom';
import styled, { keyframes } from 'styled-components';

// WEB-327 후원 완료 전환 로딩 오버레이
// 시안: Figma 2821:1128 "후원완료 페이지 로딩화면 수정_20260727_V3_bold"
export type DonateProcessStage =
  | 'confirm' // 1/3 결제 정보 확인
  | 'register' // 2/3 후원 정보 등록
  | 'almost' // 3/3 거의 다 됐어요
  | 'done' // 3/3 후원 완료
  | 'navigate'; // 완료 페이지 이동 카드

interface DonateProcessOverlayProps {
  // null이면 미표시
  stage: DonateProcessStage | null;
}

const STAGE_STEP: { [key in DonateProcessStage]: number } = {
  confirm: 1,
  register: 2,
  almost: 3,
  done: 3,
  navigate: 3,
};

const STAGE_PERCENT: { [key in DonateProcessStage]: number } = {
  confirm: 100 / 3,
  register: 200 / 3,
  almost: 100,
  done: 100,
  navigate: 100,
};

const WAITING_DESC = `안전하게 처리 중이니\n화면을 닫지 말고 잠시만 기다려 주세요.`;
const STAGE_TEXT: {
  [key in DonateProcessStage]: { title: string; desc: string };
} = {
  confirm: {
    title: `결제 정보를\n확인하고 있어요`,
    desc: WAITING_DESC,
  },
  register: {
    title: `후원 정보를\n안전하게 등록하고 있어요`,
    desc: WAITING_DESC,
  },
  almost: {
    title: `거의 다 됐어요!`,
    desc: WAITING_DESC,
  },
  done: {
    title: `후원이 완료되었어요!`,
    desc: `곧 완료 화면으로 이동합니다.`,
  },
  navigate: {
    title: `후원이 완료되었어요!`,
    desc: `완료 페이지로 이동하고 있어요.`,
  },
};

const STEP_LABELS = [`결제 확인`, `후원 등록`, `완료`];

type StepState = 'done' | 'current' | 'upcoming';

const getStepState = (stepNumber: number, activeStep: number): StepState => {
  if (stepNumber < activeStep) {
    return `done`;
  }
  if (stepNumber === activeStep) {
    return `current`;
  }
  return `upcoming`;
};

const softPulse = keyframes`
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
`;

const checkDraw = keyframes`
  from {
    stroke-dashoffset: 1;
  }
  to {
    stroke-dashoffset: 0;
  }
`;

const overlayFade = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const cardRise = keyframes`
  from {
    opacity: 0;
    transform: translateY(14px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const popIn = keyframes`
  from {
    opacity: 0;
    transform: scale(0.8);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 90;
  background: rgba(0, 0, 0, 0.6);
  -webkit-backdrop-filter: blur(16px);
  backdrop-filter: blur(16px);
  overflow-y: auto;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  animation: ${overlayFade} 0.3s ease;
`;

const Card = styled.div<{ isNavigate: boolean }>`
  width: 100%;
  max-width: 362.67px;
  margin: auto 0;
  background: #fff;
  border-radius: 7.73px;
  padding: ${({ isNavigate }) =>
    isNavigate ? `26.67px 0` : `26.67px 0 40px`};
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 21.33px;
  animation: ${cardRise} 0.4s ease;

  ${breakpoint(`tablet`)} {
    border-radius: 6.4px;
    padding: 25.6px 16px;
    gap: 19.2px;
  }
`;

const StepRow = styled.div`
  position: relative;
  width: 100%;
  padding: 0 40px;
  display: flex;
  justify-content: space-between;

  ${breakpoint(`tablet`)} {
    max-width: 236px;
    padding: 0 16px;
  }
`;

const ConnectorTrack = styled.div`
  /* 스텝 라벨 폭 차이로 원 간격이 불균등(1↔2 > 2↔3)해서,
     트랙을 StepRow 전체로 깔고 각 선분을 원 중심 실측값 ± 반지름 좌표에 배치한다.
     PC ×2/3, MO ×0.8 축소 적용 (WEB-327 2/3 축소 요청) */
  position: absolute;
  left: 0;
  right: 0;
  top: 17.43px;
  height: 2.48px;

  ${breakpoint(`tablet`)} {
    top: 15.2px;
    height: 1.6px;
  }
`;

const ConnectorLine = styled.div<{ on: boolean }>`
  position: absolute;
  top: 0;
  height: 100%;
  border-radius: 999px;
  background: ${({ on }) => (on ? `#1cabe2` : `#e5e6e8`)};
  transition: background 0.4s ease;

  /* PC: 원 중심(64.11 / 186.78 / 304) ± 반지름 13.33 */
  &:first-child {
    left: 77.45px;
    width: 96px;
  }
  &:last-child {
    left: 200.11px;
    width: 90.55px;
  }

  ${breakpoint(`tablet`)} {
    /* MO: 원 중심(36.28 / 122.28 / 204) ± 반지름 11.2 */
    &:first-child {
      left: 47.48px;
      width: 63.6px;
    }
    &:last-child {
      left: 133.48px;
      width: 59.32px;
    }
  }
`;

const StepItem = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2.67px;

  ${breakpoint(`tablet`)} {
    gap: 3.2px;
  }
`;

const StepIconArea = styled.div`
  width: 37.33px;
  height: 37.33px;
  display: flex;
  align-items: center;
  justify-content: center;

  ${breakpoint(`tablet`)} {
    width: 32px;
    height: 32px;
  }
`;

const StepCircle = styled.div<{ stepState: 'current' | 'upcoming' }>`
  width: 26.67px;
  height: 26.67px;
  border-radius: 50%;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.4s ease;
  ${({ stepState }) =>
    stepState === `current`
      ? `
        background: #fff;
        border: 1.87px solid #1cabe2;
        color: #1cabe2;
        font-weight: 700;
        font-size: 13.33px;
        letter-spacing: -0.056em;
      `
      : `
        background: #e5e6e8;
        color: #828385;
        font-weight: 400;
        font-size: 12px;
        letter-spacing: -0.0444em;
      `}

  ${breakpoint(`tablet`)} {
    width: 22.4px;
    height: 22.4px;
    font-size: 11.2px;
    letter-spacing: -0.0571em;
    ${({ stepState }) => stepState === `current` && `border-width: 1.6px;`}
  }
`;

const CurrentStepCircle = styled(StepCircle)`
  animation: ${softPulse} 1.4s ease-in-out infinite;
`;

// PC/MO 시안 값이 달라 뷰포트별로 별도 SVG를 토글 렌더링
const PcSvg = styled.svg`
  display: block;

  ${breakpoint(`tablet`)} {
    display: none;
  }
`;

const MoSvg = styled.svg`
  display: none;

  ${breakpoint(`tablet`)} {
    display: block;
  }
`;

const StepLabel = styled.div<{ active: boolean }>`
  font-size: 13.33px;
  line-height: 21.33px;
  letter-spacing: -0.058em;
  white-space: nowrap;
  transition: all 0.4s ease;
  ${({ active }) =>
    active
      ? `
        color: #2d2926;
        font-weight: 700;
      `
      : `
        color: #828385;
        font-weight: 400;
      `}

  ${breakpoint(`tablet`)} {
    font-size: 11.2px;
    line-height: 19.2px;
    letter-spacing: -0.0571em;
  }
`;

const RingArea = styled.div`
  position: relative;
  width: 154.67px;
  height: 154.67px;

  ${breakpoint(`tablet`)} {
    width: 128px;
    height: 128px;
  }
`;

const RingProgressCircle = styled.circle`
  transition: stroke-dashoffset 0.6s ease;
`;

const RingCounter = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 32px;
  font-weight: 700;
  letter-spacing: -0.01em;
  color: #2d2926;

  ${breakpoint(`tablet`)} {
    font-size: 25.6px;
  }
`;

const TextArea = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 8px;
  width: 100%;

  ${breakpoint(`tablet`)} {
    gap: 6.4px;
  }
`;

const Title = styled.strong`
  font-size: 18.67px;
  line-height: 26.67px;
  font-weight: 700;
  color: #1cabe2;
  text-align: center;
  white-space: pre-line;

  ${breakpoint(`tablet`)} {
    font-size: 16px;
    line-height: 25.6px;
    letter-spacing: -0.012em;
  }
`;

const Desc = styled.p<{ isNavigate: boolean }>`
  font-size: 13.33px;
  line-height: 18.67px;
  font-weight: 400;
  color: #2d2926;
  text-align: center;
  white-space: pre-line;

  ${breakpoint(`tablet`)} {
    font-size: 11.2px;
    line-height: ${({ isNavigate }) => (isNavigate ? `22.4px` : `19.2px`)};
    letter-spacing: -0.0571em;
  }
`;

const CompleteIconSvg = styled.svg`
  width: 53.33px;
  height: 53.33px;
  animation: ${popIn} 0.4s ease;

  ${breakpoint(`tablet`)} {
    width: 57.6px;
    height: 57.6px;
  }

  path {
    animation: ${checkDraw} 0.5s ease 0.1s both;
  }
`;

// 원형 프로그레스 (트랙 + 진행 아크 + 진행 헤드 흰 점)
const RingSvg: FC<{
  Svg: typeof PcSvg;
  size: number;
  radius: number;
  strokeWidth: number;
  dotRadius: number;
  percent: number;
}> = ({ Svg, size, radius, strokeWidth, dotRadius, percent }) => {
  const center = size / 2;
  return (
    <Svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <circle
        cx={center}
        cy={center}
        r={radius}
        fill="none"
        stroke="#e5e6e8"
        strokeWidth={strokeWidth}
      />
      <RingProgressCircle
        cx={center}
        cy={center}
        r={radius}
        fill="none"
        stroke="#1cabe2"
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        pathLength={100}
        strokeDasharray={100}
        strokeDashoffset={100 - percent}
        transform={`rotate(-90 ${center} ${center})`}
      />
      {percent < 100 && (
        <g
          style={{
            transform: `rotate(${percent * 3.6}deg)`,
            transformOrigin: `${center}px ${center}px`,
            transition: `transform 0.6s ease`,
          }}
        >
          <circle
            cx={center}
            cy={center - radius}
            r={dotRadius}
            fill="#fff"
            style={{
              filter: `drop-shadow(0px 0px 1.8px rgba(255, 255, 255, 0.35))`,
            }}
          />
        </g>
      )}
    </Svg>
  );
};

// 완료 스텝 아이콘 (파란 원 + 흰 체크) — Figma export 원본 좌표 사용
const StepDoneIcon: FC = () => (
  <>
    <PcSvg width={37.33} height={37.33} viewBox="0 0 56 56">
      <rect x={8} y={8} width={40} height={40} rx={20} fill="#1CABE2" />
      <path
        d="M36.25 22.75L25.75 33.2495L20.5 28"
        fill="none"
        stroke="#fff"
        strokeWidth={2.4}
      />
    </PcSvg>
    <MoSvg width={32} height={32} viewBox="0 0 40 40">
      <rect x={6} y={6} width={28} height={28} rx={14} fill="#1CABE2" />
      <path
        d="M26.625 16L17.875 24.7496L13.5 20.375"
        fill="none"
        stroke="#fff"
        strokeWidth={2}
      />
    </MoSvg>
  </>
);

const DonateProcessOverlay: FC<DonateProcessOverlayProps> = ({ stage }) => {
  if (!isBrowser || !stage) {
    return null;
  }

  const isNavigate = stage === `navigate`;
  const activeStep = STAGE_STEP[stage];
  const percent = STAGE_PERCENT[stage];
  const { title, desc } = STAGE_TEXT[stage];

  return createPortal(
    <Overlay>
      <Card isNavigate={isNavigate} role="status" aria-live="polite">
        {isNavigate ? (
          // 완료 카드 (체크 아이콘) — Figma export 원본 좌표 사용
          <CompleteIconSvg viewBox="0 0 80 80">
            <circle cx={40} cy={40} r={40} fill="#1cabe2" />
            <path
              d="M56.3607 29.2832L34.5447 50.7356L23.6367 40.1912"
              fill="none"
              stroke="#fff"
              strokeWidth={3.636}
              pathLength={1}
              strokeDasharray={1}
            />
          </CompleteIconSvg>
        ) : (
          <>
            <StepRow>
              <ConnectorTrack>
                <ConnectorLine on={activeStep > 1} />
                <ConnectorLine on={activeStep > 2} />
              </ConnectorTrack>
              {STEP_LABELS.map((label, index) => {
                const stepNumber = index + 1;
                const stepState = getStepState(stepNumber, activeStep);
                return (
                  <StepItem key={label}>
                    <StepIconArea>
                      {stepState === `done` && <StepDoneIcon />}
                      {stepState === `current` && (
                        <CurrentStepCircle stepState="current">
                          {stepNumber}
                        </CurrentStepCircle>
                      )}
                      {stepState === `upcoming` && (
                        <StepCircle stepState="upcoming">
                          {stepNumber}
                        </StepCircle>
                      )}
                    </StepIconArea>
                    <StepLabel active={stepState !== `upcoming`}>
                      {label}
                    </StepLabel>
                  </StepItem>
                );
              })}
            </StepRow>
            <RingArea>
              <RingSvg
                Svg={PcSvg}
                size={154.67}
                radius={70.67}
                strokeWidth={12}
                dotRadius={4}
                percent={percent}
              />
              <RingSvg
                Svg={MoSvg}
                size={128}
                radius={58.4}
                strokeWidth={9.6}
                dotRadius={3.2}
                percent={percent}
              />
              <RingCounter>{`${activeStep}/3`}</RingCounter>
            </RingArea>
          </>
        )}
        <TextArea>
          <Title>{title}</Title>
          <Desc isNavigate={isNavigate}>{desc}</Desc>
        </TextArea>
      </Card>
    </Overlay>,
    document.body,
  );
};

export default DonateProcessOverlay;
